import { BasicControl } from '../management-information/model/management-information.model'
import { IControl } from '../management-information/store/management-information.reducer'
import { SortTableColumnDef } from '@shared/sort-table/sort-table.model'
import { dateToIsoString } from 'src/app/shared/util/date'

export interface TrackingData {
  acctName: string
  acctid: string
  clientName: string
  clientId: number
  year: string
  programName: string
  programId: number
  businessUnit: string
  inceptionDate: string | null
  opportunityStage: string
  structureCount: number
  reinsurerCount: number
  marketingList: string | null
  quoteCount: number
  fotCount: number
  layerCount: number
  signedLines: string | null
  submittedOT: string | null
  complete: string
  details?: TrackingDetailsData[] | null
  reinsurerNames?: string[]
}

export interface TrackingDetailsData {
  programId: number
  structureId: number
  structureName: string
  layerDetails: TrackingDetailsLayerData[] | null
}

export interface TrackingDetailsLayerData {
  layerName: string
  quoteCount: number
  written: string
  order: string
  fotCount: number
  signed: string
}

export interface TrackingSelectors {
  clientName: IControl
  year: IControl
  businessUnit: IControl
  reinsurerNames: IControl
  complete: IControl
}

export interface TrackingResponseData {
  acctName: string
  acctid: string
  carrierName: string
  carrierId: number
  year: number
  programName: string
  programId: number
  oppBusinessUnit: string
  oppStage: string
  oppInception: Date
  structureCount: number
  quoteCount: number
  fotCount: number
  layerCount: number
  assignedLinesCount: number
  reinsurerCount: number
  minOTSubmissionDate: null
  firstExportDate: Date | null
  lastExportDate: Date | null
  completeDate: Date | null
  reinsurerNames: string[] | null
}

export interface TrackingDetailsResponseData {
  year: number
  programId: number
  structureId: number
  structureLabel: string
  riskSectionId: number
  layerRef: string
  layerName: string
  inceptionDate: Date | null
  contractCreatedDate: Date | null
  otSubmissionDate: Date | null
  fotInsertDate: Date | null
  fotCount: number | null
  quoteInsertDate: Date | null
  quoteCount: number | null
  orderTotal: number
  signedTotal: number | null
  writtenTotal: number | null
}

export interface TrackingPreferences {
  id?: number
  label: string
  clientName: string[]
  year: string[]
  businessUnit: string[]
  complete: string[]
  reinsurerNames: string[]
  minDateValue: string
  maxDateValue: string
}

export interface TrackingStatusResponse {
  status: TrackingStatus
  categoryName: string
}

export interface TrackingStatus {
  id: number
  opportunityId: string
  statusCategoryId: number
  value: string
  date: string | null
  isCurrent: boolean
  updateSource: 'Manual' | 'Programmatic' | string
  audit_update_dt: string
  audit_insert_dt: string
  audit_created_by: string
  audit_updated_by: string
}
export interface TeamBulkRequest {
  role: string
  email_ids: string[]
}

export interface ColumnRequest {
  category: string
  value: string | string[]
  date?: string | null
}
export interface UpdateColumnValueResponse {
  opportunityId: string
  category: string
  text: string
}

export interface CommentRequest {
  category: string
  comment: string
}
export interface CommentResponse {
  opportunityId: string
  category: string
  comment: string
}
export interface Comment {
  author: string
  authorEmail: string
  text: string
  createdDate: string
}

export interface Column {
  columnName: string
  value: string
  updatedDate: string | null
  updatedSource: string
  comments: Comment[]
  valueHistory?: ValueHistoryItem[]
}

interface ValueHistoryItem {
  value: string
  updatedDate: string
}

export interface userRole {
  email_id: string
  name: string
}

export interface TrackingViewsData {
  placementProgress?: any
  opportunityId: string
  client: string
  year: string
  program: string
  businessUnit: string
  accountExecutive: userRole[]
  support: userRole[]
  analytics: userRole[]
  columns: Column[]
  inceptionDate: string
  reinsurerNames: string[]
  auditColumns: Column[]
}

export interface TrackingViewsResponseData {
  opportunityId: string
  client: string
  year: string
  program: string
  businessUnit: string
  accountExecutive: userRole[]
  support: userRole[]
  analytics: userRole[]
  columns: Column[]
  inceptionDate: string
  reinsurerNames: string[]
  auditColumns: Column[]
}
export const DEFAULT_TRACKING_SELECTORS: TrackingSelectors = {
  clientName: {
    columnName: 'clientName',
    name: 'clientName',
    id: 'clientName',
    selectedValues: [],
    values: [],
    allValues: [],
  },
  year: {
    columnName: 'year',
    name: 'year',
    id: 'year',
    selectedValues: [],
    values: [],
    allValues: [],
  },
  businessUnit: {
    columnName: 'businessUnit',
    name: 'businessUnit',
    id: 'businessUnit',
    selectedValues: [],
    values: [],
    allValues: [],
  },
  reinsurerNames: {
    columnName: 'reinsurerNames',
    name: 'reinsurerNames',
    id: 'reinsurerNames',
    selectedValues: [],
    values: [],
    allValues: [],
  },
  complete: {
    columnName: 'complete',
    name: 'complete',
    id: 'complete',
    selectedValues: [],
    values: [],
    allValues: [],
  },
}

export const DEFAULT_INCEPTION_DATE: BasicControl = {
  name: 'Inception Date',
  columnName: 'inceptionDate',
  minValue: dateToIsoString(getStartOfYear()),
  maxValue: dateToIsoString(getEndOfCurrentMonth()),
}

function getStartOfYear() {
  return new Date(new Date().getFullYear(), 0, 1)
}

function getEndOfCurrentMonth() {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 0)
}

export const TRACKING_COLUMNS: SortTableColumnDef[] = [
  {
    label: 'Client Name',
    id: 'clientName',
    valueType: 'text',
  },
  {
    label: 'Program Name',
    id: 'programName',
    valueType: 'text',
  },
  {
    label: 'Program Year',
    id: 'year',
    valueType: 'text',
  },
  {
    label: 'Inception Date',
    id: 'inceptionDate',
    valueType: 'text',
  },
  {
    label: 'Opportunity Stage',
    id: 'opportunityStage',
    valueType: 'text',
  },
  {
    label: 'Structure Count',
    id: 'structureCount',
    valueType: 'numeric',
  },
  {
    label: 'Reinsurer Count',
    id: 'reinsurerCount',
    valueType: 'numeric',
  },
  {
    label: 'Marketing List',
    id: 'marketingList',
    valueType: 'text',
  },
  {
    label: 'Quote Count',
    id: 'quoteCount',
    valueType: 'numeric',
  },
  {
    label: 'FOT Count',
    id: 'fotCount',
    valueType: 'numeric',
  },
  {
    label: 'Signed Lines',
    id: 'signedLines',
    valueType: 'text',
  },
  {
    label: 'Submitted to OT',
    id: 'submittedOT',
    valueType: 'text',
  },
  {
    label: 'Complete',
    id: 'complete',
    valueType: 'text',
  },
]

export const TRACKING_VIEW_COLUMNS: SortTableColumnDef[] = [
  {
    label: 'Client',
    id: 'clientName',
    valueType: 'text',
    pinned: true,
  },
  {
    label: 'Program',
    id: 'programName',
    valueType: 'text',
    pinned: true,
  },
  {
    label: 'Placement Progress',
    id: 'placementProgress',
    valueType: 'numeric',
    pinned: true,
  },
  {
    label: 'Business Unit',
    id: 'businessUnit',
    valueType: 'text',
  },
  {
    label: 'Inception Date',
    id: 'inceptionDate',
    valueType: 'text',
  },
  {
    label: 'Client Instructions',
    id: 'clientInstructions',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'Received', viewValue: 'Received' },
      { value: 'N/A', viewValue: 'N/A' },
    ],
  },
  {
    label: 'Submission',
    id: 'submission',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'Requested', viewValue: 'Requested' },
      { value: 'Received from Client', viewValue: 'Received from Client' },
      { value: 'Sent to Reinsurers', viewValue: 'Sent to Reinsurers' },
      { value: 'N/A', viewValue: 'N/A' },
    ],
  },
  {
    label: 'Draft Contract',
    id: 'draftContract',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'In Progress', viewValue: 'In Progress' },
      { value: 'SPOE', viewValue: 'SPOE' },
      { value: 'Sent to Client', viewValue: 'Sent to Client' },
      { value: 'Client Signoff', viewValue: 'Client Signoff' },
    ],
  },
  {
    label: 'Quote',
    id: 'quote',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'Requests Sent', viewValue: 'Requests Sent' },
      { value: 'Received from Reinsurers', viewValue: 'Received from Reinsurers' },
      { value: 'Sent to Client', viewValue: 'Sent to Client' },
      { value: 'N/A', viewValue: 'N/A' },
    ],
  },
  {
    label: 'FOTS',
    id: 'FOTS',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'Received from Client', viewValue: 'Received from Client' },
      { value: 'Sent to Reinsurers', viewValue: 'Sent to Reinsurers' },
    ],
  },
  {
    label: 'Signings',
    id: 'signings',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'Approved by Client', viewValue: 'Approved by Client' },
      { value: 'Confirmed with Reinsurers', viewValue: 'Confirmed with Reinsurers' },
    ],
  },
  {
    label: 'Final Contract',
    id: 'finalContract',
    valueType: 'dropdown',
    references: [
      { value: ' ', viewValue: ' ' },
      { value: 'In Progress', viewValue: 'In Progress' },
      { value: 'SPOE', viewValue: 'SPOE' },
      { value: 'Sent to Client', viewValue: 'Sent to Client' },
      { value: 'Sent to Reinsurers', viewValue: 'Sent to Reinsurers' },
      { value: 'Signed', viewValue: 'Signed' },
    ],
  },
  {
    label: 'Account Executive',
    id: 'accountExecutive',
    valueType: 'multiselect',
    references: [],
  },
  {
    label: 'Support',
    id: 'support',
    valueType: 'multiselect',
    references: [],
  },
  {
    label: 'Analytics',
    id: 'analytics',
    valueType: 'multiselect',
    references: [],
  },
  {
    label: '+',
    id: 'complete',
    valueType: 'text',
  },
]

export const TRACKING_DETAILS_COLUMNS: SortTableColumnDef[] = [
  {
    label: 'Layer Name',
    id: 'layerName',
    valueType: 'text',
    width: '15rem',
  },
  {
    label: 'Quote Count',
    id: 'quoteCount',
    valueType: 'numeric',
    width: '5rem',
  },
  {
    label: 'Written %',
    id: 'written',
    valueType: 'text',
    width: '5rem',
  },
  {
    label: 'Order %',
    id: 'order',
    valueType: 'text',
    width: '5rem',
  },
  {
    label: 'FOT Count',
    id: 'fotCount',
    valueType: 'numeric',
    width: '5rem',
  },
  {
    label: 'Signed %',
    id: 'signed',
    valueType: 'text',
    width: '5rem',
  },
]

export interface DataCounts {
  distinctClients: number
  distinctPrograms: number
  distinctLayers: number
  distinctQuotes: number
  distinctFots: number
}

export interface DataCountValues {
  label: string
  id: string
  tooltip: string
  count?: number
}

export const DATA_COUNT: DataCountValues[] = [
  {
    label: 'Clients',
    id: 'distinctClients',
    tooltip:
      'Count exclusions: Records where the Opportunity Stage = Prospect, RFP/Pitch, NTU and/or where the Quotes AND FOTs are both 0, wil be excluded from the count.',
  },
  {
    label: 'Programs',
    id: 'distinctPrograms',
    tooltip:
      'Count exclusions: Records where the Opportunity Stage = Prospect, RFP/Pitch, NTU and/or where the Quotes AND FOTs are both 0, wil be excluded from the count.',
  },
  {
    label: 'Layers',
    id: 'distinctLayers',
    tooltip:
      'Count exclusions: Records where the Opportunity Stage = Prospect, RFP/Pitch, NTU and/or where the Quotes AND FOTs are both 0, wil be excluded from the count.',
  },
  {
    label: 'Quotes',
    id: 'distinctQuotes',
    tooltip:
      'Count exclusions: Records where the Opportunity Stage = Prospect, RFP/Pitch, NTU and/or where the Quotes AND FOTs are both 0, wil be excluded from the count.',
  },
  {
    label: 'FOTs',
    id: 'distinctFots',
    tooltip:
      'Count exclusions: Records where the Opportunity Stage = Prospect, RFP/Pitch, NTU and/or where the Quotes AND FOTs are both 0, wil be excluded from the count.',
  },
]
